"use client";

/**
 * Shared Spinner Component
 * Reusable loading spinner with consistent styling
 */

import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/shared/core/utils";

// ============================================================================
// TYPES
// ============================================================================

type SpinnerSize = "xs" | "sm" | "md" | "lg" | "xl";
type SpinnerVariant = "default" | "primary" | "white" | "muted";

interface SpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Visual variant */
  variant?: SpinnerVariant;
  /** Additional className */
  className?: string;
  /** Use Lucide Loader2 icon instead of div spinner */
  useIcon?: boolean;
}

// ============================================================================
// SIZE & VARIANT MAPS
// ============================================================================

const sizeMap: Record<SpinnerSize, string> = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
};

const borderSizeMap: Record<SpinnerSize, string> = {
  xs: "border",
  sm: "border-2",
  md: "border-2",
  lg: "border-4",
  xl: "border-4",
};

const variantMap: Record<SpinnerVariant, string> = {
  default: "border-muted border-t-primary",
  primary: "border-primary border-t-transparent",
  white: "border-white/30 border-t-white",
  muted: "border-muted-foreground/30 border-t-muted-foreground",
};

const iconVariantMap: Record<SpinnerVariant, string> = {
  default: "text-primary",
  primary: "text-primary",
  white: "text-white",
  muted: "text-muted-foreground",
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Reusable loading spinner component
 *
 * @example
 * // Default spinner
 * <Spinner />
 *
 * // Large white spinner (for dark backgrounds)
 * <Spinner size="lg" variant="white" />
 *
 * // Icon-based spinner
 * <Spinner useIcon size="sm" />
 */
export function Spinner({
  size = "md",
  variant = "default",
  className,
  useIcon = false,
}: SpinnerProps) {
  if (useIcon) {
    return (
      <Loader2
        className={cn(
          "animate-spin",
          sizeMap[size],
          iconVariantMap[variant],
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "animate-spin rounded-full",
        sizeMap[size],
        borderSizeMap[size],
        variantMap[variant],
        className
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

/**
 * Centered spinner with optional text
 */
export function SpinnerWithText({
  size = "md",
  variant = "default",
  text,
  className,
}: SpinnerProps & { text?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2",
        className
      )}
    >
      <Spinner size={size} variant={variant} />
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
  );
}

export default Spinner;
